import { v4 as uuid } from "uuid";
import { useEffect, useState } from "react";
import { DataSheetGrid } from "react-datasheet-grid";

// Import the style only once in your app!
import "react-datasheet-grid/dist/style.css";
import "./styles.css";

import { Operation } from "react-datasheet-grid/dist/types";
import { EmployeeLineItem } from "@interfaces/employee";
import {
  LabourHourLineItem,
  LabourHourTableRow,
  createDefaultLabourHour,
} from "@interfaces/labourHour";
import { Button, Grid, Typography } from "@mui/material";
import { StyledLoadingButton } from "@stories/atoms/StyledButton/StyledButton";
import { SocialCommitmentTypeHour, TargetUnit } from "@interfaces/commitment";
import { ContractorPackageLineItem } from "@interfaces/contractorPackage";
import { getLabourHourColumns } from "./LabourHourColumns";
import { getEmployeeFactors } from "decoders/employee";

interface LabourHoursSheetProps {
  loading: boolean;
  reportId: string;
  contractorPackage: ContractorPackageLineItem;
  labourHours: LabourHourLineItem[];
  employees: EmployeeLineItem[];
  handleUpsertLabourHours: (labourHours: LabourHourLineItem[]) => void;
  handleDeleteLabourHours: (labourHours: LabourHourLineItem[]) => void;
}
export const LabourHourSheet = ({
  loading,
  reportId,
  labourHours,
  employees,
  contractorPackage,
  handleUpsertLabourHours,
  handleDeleteLabourHours,
}: LabourHoursSheetProps) => {
  const [data, setData] = useState<LabourHourTableRow[]>([]);

  const commitments = contractorPackage.commitments.filter(
    (c) => c.targetUnit == TargetUnit.Hour
  );

  const updateSocialFactors = (rowId: number, employeeId: string) => {
    const employee = employees.find((e) => e.id === employeeId);
    if (employee) {
      const socialFactorIds = getEmployeeFactors(contractorPackage, employee);
      const labourHour = data[rowId];
      labourHour.employeeId = employeeId;
      labourHour.employeeName = employee.employeeName;
      labourHour.employmentType = employee.employmentType;
      labourHour.gender = employee.gender;

      for (const sf of commitments) {
        (labourHour as unknown as any)[sf.id] = socialFactorIds.includes(sf.id);
      }
      setData(data);
    }
  };

  const columns = getLabourHourColumns(
    employees,
    commitments,
    updateSocialFactors,
    false //FIX THIS - Seems to be a bug in the library, possible memoistation issue. Component stays disabled even when loading is false
  );

  if (data.length < 10) {
    for (let i = 0; i < 10 - data.length; i++) {
      data.push({ id: uuid(), empty: true } as LabourHourTableRow);
    }
    setData(data);
  } else {
    if (data.length > 0) {
      if (!data[data.length - 1].empty) {
        data.push({ id: uuid(), empty: true } as LabourHourTableRow);
      }
    }
  }

  const regenData = () => {
    const labourHourRowItems = labourHours.map((l) => {
      const newTableRow = {
        id: l.id,
        amount: l.amount,
        employeeId: l.employeeId,
        employeeName: l.employeeName,
        gender: l.gender,
        employmentType: l.employmentType,
        dirty: false,
        invalid: false,
        empty: false,
      } as LabourHourTableRow;
      for (const sf of commitments) {
        (newTableRow as unknown as any)[sf.id] = !!l.socialFactors.find(
          (lsf) => sf.id === lsf
        );
      }

      return newTableRow;
    }) as LabourHourTableRow[];
    setData(labourHourRowItems);
  };

  useEffect(() => {
    regenData();
  }, [labourHours]);

  const validateLabourHourTableRow = (labourHour: LabourHourTableRow) => {
    const employeeNameValid = Boolean(
      employees.find((e) => e.id === labourHour.employeeId)
    );

    return (
      !labourHour.empty &&
      (!labourHour.employeeId || !labourHour.amount || !employeeNameValid)
    );
  };

  const isEmptyLabourHourTableRow = (labourHour: LabourHourTableRow) => {
    return (
      !labourHour.employeeName && !labourHour.amount && !labourHour.employeeId
    );
  };

  const [deletedRowIds, setDeletedRowIds] = useState(new Set<string>());
  const [updatedRowIds, setUpdatedRowIds] = useState(new Set<string>());

  const onDataChange = (
    data: LabourHourTableRow[],
    operations: Operation[]
  ) => {
    data.forEach((d, index) => {
      d.empty = isEmptyLabourHourTableRow(d);
      d.invalid = validateLabourHourTableRow(d);

      const employee = employees.find((e) => e.id === d.employeeId);
      if (employee) {
        data[index] = {
          ...d,
          employeeName: employee.employeeName,
          gender: employee.gender,
          employmentType: employee.employmentType,
        };
      }
    });

    for (const operation of operations) {
      if (operation.type === "CREATE" || operation.type === "UPDATE") {
        data
          .slice(operation.fromRowIndex, operation.toRowIndex)
          .forEach((d) => {
            d.dirty = true;
            setUpdatedRowIds((prev) => {
              if (d.invalid) {
                return prev;
              }
              return new Set(prev.add(d.id));
            });
          });
      }
      if (operation.type === "DELETE") {
        const tableIds = data.map((d) => d.id);
        data
          .slice(operation.fromRowIndex, operation.toRowIndex)
          .forEach((d) => {
            setDeletedRowIds((prev) => {
              const idsToDelete = labourHours
                .filter((lh) => !tableIds.includes(lh.id))
                .map((lh) => lh.id);
              for (const id of idsToDelete) {
                prev = prev.add(id);
              }
              return new Set(prev);
            });
          });
      }
    }
    setData(data);
  };

  const handleSave = async () => {
    const labourToUpsert = data.filter((r) => r.id && updatedRowIds.has(r.id));
    const validRows = labourToUpsert.filter((r) => !r.invalid && !r.empty);

    const labourHourLineItemsToUpsert: LabourHourLineItem[] = [];
    validRows.forEach((lh) => {
      const newSocialFactors = [];
      for (const sc of commitments) {
        if ((lh as unknown as any)[sc.id]) {
          newSocialFactors.push(sc.id);
        }
      }

      const originalLabourHour = labourHours.find((o) => o.id === lh.id);
      if (originalLabourHour) {
        originalLabourHour.amount = lh.amount || originalLabourHour.amount;
        originalLabourHour.employeeId =
          lh.employeeId || originalLabourHour.employeeId;
        const employee = employees.find(
          (e) => e.id === originalLabourHour.employeeId
        );
        if (employee) {
          originalLabourHour.employeeId = employee.id;
          originalLabourHour.gender = employee.gender;
          originalLabourHour.employeeName = employee.employeeName;
          originalLabourHour.employmentType = employee.employmentType;
        }
        originalLabourHour.socialFactors =
          newSocialFactors as SocialCommitmentTypeHour[];

        labourHourLineItemsToUpsert.push(originalLabourHour);
      } else {
        const employee = employees.find((e) => e.id === lh.employeeId);
        const defaultLabourHour = createDefaultLabourHour(
          reportId,
          contractorPackage,
          employee
        );
        labourHourLineItemsToUpsert.push({
          ...defaultLabourHour,
          id: lh.id,
          employeeId: lh.employeeId || defaultLabourHour.employeeId,
          employeeName: lh.employeeName || defaultLabourHour.employeeName,
          gender: lh.gender || defaultLabourHour.gender,
          amount: lh.amount || defaultLabourHour.amount,
          socialFactors: newSocialFactors as SocialCommitmentTypeHour[],
        });
      }
    });
    if (labourHourLineItemsToUpsert.length > 0) {
      await handleUpsertLabourHours(labourHourLineItemsToUpsert);
    }

    const labourHoursToDelete = labourHours.filter((r) =>
      deletedRowIds.has(r.id)
    );
    if (labourHoursToDelete.length > 0) {
      await handleDeleteLabourHours(labourHoursToDelete);
    }
    setDeletedRowIds(new Set());
    setUpdatedRowIds(new Set());
  };

  return (
    <>
      <Grid container spacing={2} sx={{ padding: "10px" }}>
        <Grid item xs={12} md={12}>
          <DataSheetGrid<LabourHourTableRow>
            autoAddRow
            value={data}
            onChange={onDataChange}
            columns={columns}
            // rowClassName={({ rowIndex }) => {
            //   return rowIndex % 2 === 0 ? "row-warning" : "";
            // }}
            cellClassName={({ rowData, rowIndex }) => {
              const row = rowData as LabourHourTableRow;
              if (row.empty) {
                return "row-font";
              }
              if (row.invalid) {
                return "row-error row-font";
              }
              return row.dirty ? "row-warning row-font" : "";
            }}
          />
        </Grid>
        <Grid
          container
          alignItems="center"
          justifyContent="flex-end"
          spacing={2}
          mt={2}
        >
          <Grid item>
            <StyledLoadingButton
              onClick={handleSave}
              loading={loading}
              disabled={loading}
            >
              Submit
            </StyledLoadingButton>
          </Grid>
          <Grid item>
            <Typography variant="body2" sx={{ m: 0 }}>
              or
            </Typography>
          </Grid>
          <Grid item>
            <Button variant="text" onClick={regenData}>
              Reset
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
